import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type AccessibilitySettings = {
  // Text
  fontSize: number;
  letterSpacing: 'normal' | 'wide';
  lineHeight: 'normal' | 'large';
  textAlign: 'default' | 'left' | 'center' | 'right';
  readableFont: boolean;

  // Visual
  contrastMode: 'none' | 'blackYellow' | 'blackWhite';
  grayscale: boolean;
  invertColors: boolean;
  hideImages: boolean;
  highlightLinks: boolean;
  highlightHeadings: boolean;

  // Interaction
  keyboardNavigation: boolean;
  stopAnimations: boolean;
  bigCursor: boolean;
  readingGuide: boolean;
};

type AccessibilityContextValue = {
  settings: AccessibilitySettings;
  updateFontSize: (size: number) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  setLetterSpacing: (spacing: 'normal' | 'wide') => void;
  setLineHeight: (height: 'normal' | 'large') => void;
  setTextAlign: (align: 'default' | 'left' | 'center' | 'right') => void;
  toggleReadableFont: () => void;
  setContrastMode: (mode: 'none' | 'blackYellow' | 'blackWhite') => void;
  toggleGrayscale: () => void;
  toggleInvertColors: () => void;
  toggleHideImages: () => void;
  toggleHighlightLinks: () => void;
  toggleHighlightHeadings: () => void;
  toggleKeyboardNavigation: () => void;
  toggleStopAnimations: () => void;
  toggleBigCursor: () => void;
  toggleReadingGuide: () => void;
  resetAll: () => void;
};

const defaultSettings: AccessibilitySettings = {
  fontSize: 16,
  letterSpacing: 'normal',
  lineHeight: 'normal',
  textAlign: 'default',
  readableFont: false,
  contrastMode: 'none',
  grayscale: false,
  invertColors: false,
  hideImages: false,
  highlightLinks: false,
  highlightHeadings: false,
  keyboardNavigation: false,
  stopAnimations: false,
  bigCursor: false,
  readingGuide: false,
};

const AccessibilityContext = createContext<AccessibilityContextValue | null>(
  null,
);

type ProviderProps = {
  children: ReactNode;
};

const STORAGE_KEY = '@accessibility_settings_v2';
const WEB_STORAGE_KEY = 'kursi_accessibility_settings_v2';

function sanitizeSettings(data: any): AccessibilitySettings | null {
  if (!data || typeof data !== 'object') return null;

  const sanitized: AccessibilitySettings = { ...defaultSettings };

  if (typeof data.fontSize === 'number' && Number.isFinite(data.fontSize)) {
    sanitized.fontSize = Math.max(12, Math.min(24, Math.round(data.fontSize)));
  }

  if (data.letterSpacing === 'normal' || data.letterSpacing === 'wide') {
    sanitized.letterSpacing = data.letterSpacing;
  }

  if (data.lineHeight === 'normal' || data.lineHeight === 'large') {
    sanitized.lineHeight = data.lineHeight;
  }

  if (['default', 'left', 'center', 'right'].includes(data.textAlign)) {
    sanitized.textAlign = data.textAlign;
  }

  if (['none', 'blackYellow', 'blackWhite'].includes(data.contrastMode)) {
    sanitized.contrastMode = data.contrastMode;
  }

  const boolKeys: (keyof Omit<AccessibilitySettings, 'fontSize' | 'letterSpacing' | 'lineHeight' | 'textAlign' | 'contrastMode'>)[] = [
    'readableFont',
    'grayscale',
    'invertColors',
    'hideImages',
    'highlightLinks',
    'highlightHeadings',
    'keyboardNavigation',
    'stopAnimations',
    'bigCursor',
    'readingGuide',
  ];

  for (const key of boolKeys) {
    if (typeof data[key] === 'boolean') {
      sanitized[key] = data[key];
    }
  }

  return sanitized;
}

function loadFromLocalStorage(key: string): AccessibilitySettings | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    return sanitizeSettings(parsed);
  } catch (error) {
    console.warn(`Failed to load from localStorage (${key}):`, error);
    return null;
  }
}

function saveToLocalStorage(key: string, settings: AccessibilitySettings): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(settings));
  } catch (error) {
    console.warn(`Failed to save to localStorage (${key}):`, error);
  }
}

function removeFromLocalStorage(key: string): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove from localStorage (${key}):`, error);
  }
}

function applyWebEffects(settings: AccessibilitySettings): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;

  const html = window.document.documentElement;
  const body = window.document.body;

  if (!window.document.getElementById('kursi-a11y-styles')) {
    const style = window.document.createElement('style');
    style.id = 'kursi-a11y-styles';
    style.textContent = `
      html.a11y-readable-font,
      html.a11y-readable-font *,
      body.a11y-readable-font,
      body.a11y-readable-font * {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif !important;
      }

      html.a11y-letter-spacing-wide,
      html.a11y-letter-spacing-wide *,
      body.a11y-letter-spacing-wide,
      body.a11y-letter-spacing-wide * {
        letter-spacing: 0.12em !important;
      }

      html.a11y-line-height-large,
      html.a11y-line-height-large *,
      body.a11y-line-height-large,
      body.a11y-line-height-large * {
        line-height: 1.8 !important;
      }

      html.a11y-text-align-left *,
      body.a11y-text-align-left * {
        text-align: left !important;
      }

      html.a11y-text-align-center *,
      body.a11y-text-align-center * {
        text-align: center !important;
      }

      html.a11y-text-align-right *,
      body.a11y-text-align-right * {
        text-align: right !important;
      }

      html.a11y-contrast-black-yellow,
      body.a11y-contrast-black-yellow {
        background-color: #000000 !important;
      }

      html.a11y-contrast-black-yellow *,
      body.a11y-contrast-black-yellow * {
        background-color: #FFFF00 !important;
        color: #000000 !important;
        border-color: #000000 !important;
      }

      html.a11y-contrast-black-white,
      body.a11y-contrast-black-white {
        background-color: #000000 !important;
      }

      html.a11y-contrast-black-white *,
      body.a11y-contrast-black-white * {
        background-color: #FFFFFF !important;
        color: #000000 !important;
        border-color: #000000 !important;
      }

      html.a11y-highlight-links a,
      html.a11y-highlight-links [role="link"],
      html.a11y-highlight-links button,
      html.a11y-highlight-links [role="button"],
      body.a11y-highlight-links a,
      body.a11y-highlight-links [role="link"],
      body.a11y-highlight-links button,
      body.a11y-highlight-links [role="button"] {
        text-decoration: underline !important;
        outline: 2px solid rgba(142, 95, 255, 0.4) !important;
        outline-offset: 2px !important;
        background-color: rgba(142, 95, 255, 0.05) !important;
      }

      html.a11y-highlight-headings h1,
      html.a11y-highlight-headings h2,
      html.a11y-highlight-headings h3,
      html.a11y-highlight-headings h4,
      html.a11y-highlight-headings h5,
      html.a11y-highlight-headings h6,
      html.a11y-highlight-headings [role="heading"],
      body.a11y-highlight-headings h1,
      body.a11y-highlight-headings h2,
      body.a11y-highlight-headings h3,
      body.a11y-highlight-headings h4,
      body.a11y-highlight-headings h5,
      body.a11y-highlight-headings h6,
      body.a11y-highlight-headings [role="heading"] {
        outline: 3px solid rgba(142, 95, 255, 0.6) !important;
        outline-offset: 4px !important;
        padding: 8px !important;
        border-radius: 8px !important;
      }

      html.a11y-hide-images img,
      html.a11y-hide-images [role="img"],
      body.a11y-hide-images img,
      body.a11y-hide-images [role="img"] {
        opacity: 0 !important;
        visibility: hidden !important;
      }

      html.a11y-stop-animations,
      html.a11y-stop-animations *,
      body.a11y-stop-animations,
      body.a11y-stop-animations * {
        animation: none !important;
        transition: none !important;
      }

      html.a11y-keyboard *:focus,
      html.a11y-keyboard *:focus-visible,
      body.a11y-keyboard *:focus,
      body.a11y-keyboard *:focus-visible {
        outline: 3px solid #8E5FFF !important;
        outline-offset: 2px !important;
        box-shadow: 0 0 8px rgba(142, 95, 255, 0.6) !important;
      }

      #kursi-reading-guide {
        position: fixed;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(to right, transparent, #8E5FFF, transparent);
        pointer-events: none;
        z-index: 999999;
        box-shadow: 0 0 10px rgba(142, 95, 255, 0.8);
      }
    `;
    window.document.head.appendChild(style);
  }

  const filters: string[] = [];

  if (settings.grayscale) {
    filters.push('grayscale(1)');
  }

  if (settings.invertColors) {
    filters.push('invert(1) hue-rotate(180deg)');
  }

  const filterValue = filters.join(' ');
  html.style.filter = filterValue;
  body.style.filter = filterValue;

  html.classList.toggle('a11y-readable-font', settings.readableFont);
  html.classList.toggle('a11y-letter-spacing-wide', settings.letterSpacing === 'wide');
  html.classList.toggle('a11y-line-height-large', settings.lineHeight === 'large');
  html.classList.toggle('a11y-text-align-left', settings.textAlign === 'left');
  html.classList.toggle('a11y-text-align-center', settings.textAlign === 'center');
  html.classList.toggle('a11y-text-align-right', settings.textAlign === 'right');
  html.classList.toggle('a11y-contrast-black-yellow', settings.contrastMode === 'blackYellow');
  html.classList.toggle('a11y-contrast-black-white', settings.contrastMode === 'blackWhite');
  html.classList.toggle('a11y-highlight-links', settings.highlightLinks);
  html.classList.toggle('a11y-highlight-headings', settings.highlightHeadings);
  html.classList.toggle('a11y-hide-images', settings.hideImages);
  html.classList.toggle('a11y-stop-animations', settings.stopAnimations);
  html.classList.toggle('a11y-keyboard', settings.keyboardNavigation);

  body.classList.toggle('a11y-readable-font', settings.readableFont);
  body.classList.toggle('a11y-letter-spacing-wide', settings.letterSpacing === 'wide');
  body.classList.toggle('a11y-line-height-large', settings.lineHeight === 'large');
  body.classList.toggle('a11y-text-align-left', settings.textAlign === 'left');
  body.classList.toggle('a11y-text-align-center', settings.textAlign === 'center');
  body.classList.toggle('a11y-text-align-right', settings.textAlign === 'right');
  body.classList.toggle('a11y-contrast-black-yellow', settings.contrastMode === 'blackYellow');
  body.classList.toggle('a11y-contrast-black-white', settings.contrastMode === 'blackWhite');
  body.classList.toggle('a11y-highlight-links', settings.highlightLinks);
  body.classList.toggle('a11y-highlight-headings', settings.highlightHeadings);
  body.classList.toggle('a11y-hide-images', settings.hideImages);
  body.classList.toggle('a11y-stop-animations', settings.stopAnimations);
  body.classList.toggle('a11y-keyboard', settings.keyboardNavigation);

  if (settings.bigCursor) {
    const cursorSvg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath d='M2 2 L2 28 L12 22 L16 30 L20 28 L16 20 L26 20 Z' fill='%238E5FFF' stroke='white' stroke-width='2'/%3E%3C/svg%3E`;
    html.style.cursor = `url('${cursorSvg}') 2 2, auto`;
    body.style.cursor = `url('${cursorSvg}') 2 2, auto`;
  } else {
    html.style.cursor = '';
    body.style.cursor = '';
  }

  if (settings.readingGuide) {
    if (!window.document.getElementById('kursi-reading-guide')) {
      const guide = window.document.createElement('div');
      guide.id = 'kursi-reading-guide';
      guide.style.top = '-100px';
      window.document.body.appendChild(guide);

      const handleMouseMove = (e: MouseEvent) => {
        guide.style.top = `${e.clientY}px`;
      };

      window.addEventListener('mousemove', handleMouseMove);
      (guide as any).__mouseMoveHandler = handleMouseMove;
    }
  } else {
    const guide = window.document.getElementById('kursi-reading-guide');
    if (guide) {
      const handler = (guide as any).__mouseMoveHandler;
      if (handler) {
        window.removeEventListener('mousemove', handler);
      }
      guide.remove();
    }
  }
}

export function AccessibilityProvider({ children }: ProviderProps) {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      saveSettings(settings);
    }
  }, [settings, isLoaded]);

  useEffect(() => {
    if (isLoaded && Platform.OS === 'web') {
      applyWebEffects(settings);
    }
  }, [settings, isLoaded]);

  const loadSettings = async () => {
    try {
      let loadedSettings: AccessibilitySettings | null = null;

      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        loadedSettings = sanitizeSettings(parsed);
      }

      if (!loadedSettings && Platform.OS === 'web') {
        loadedSettings = loadFromLocalStorage(WEB_STORAGE_KEY);
      }

      if (loadedSettings) {
        setSettings(loadedSettings);
      }
    } catch (error) {
      console.error('Failed to load accessibility settings:', error);
    } finally {
      setIsLoaded(true);
    }
  };

  const saveSettings = async (newSettings: AccessibilitySettings) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));

      if (Platform.OS === 'web') {
        saveToLocalStorage(WEB_STORAGE_KEY, newSettings);
      }
    } catch (error) {
      console.error('Failed to save accessibility settings:', error);
    }
  };

  const updateFontSize = (size: number) =>
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.max(12, Math.min(24, size)),
    }));

  const increaseFontSize = () =>
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.min(prev.fontSize + 2, 24),
    }));

  const decreaseFontSize = () =>
    setSettings((prev) => ({
      ...prev,
      fontSize: Math.max(prev.fontSize - 2, 12),
    }));

  const setLetterSpacing = (spacing: 'normal' | 'wide') =>
    setSettings((prev) => ({ ...prev, letterSpacing: spacing }));

  const setLineHeight = (height: 'normal' | 'large') =>
    setSettings((prev) => ({ ...prev, lineHeight: height }));

  const setTextAlign = (align: 'default' | 'left' | 'center' | 'right') =>
    setSettings((prev) => ({ ...prev, textAlign: align }));

  const toggleReadableFont = () =>
    setSettings((prev) => ({ ...prev, readableFont: !prev.readableFont }));

  const setContrastMode = (mode: 'none' | 'blackYellow' | 'blackWhite') =>
    setSettings((prev) => ({ ...prev, contrastMode: mode }));

  const toggleGrayscale = () =>
    setSettings((prev) => ({ ...prev, grayscale: !prev.grayscale }));

  const toggleInvertColors = () =>
    setSettings((prev) => ({ ...prev, invertColors: !prev.invertColors }));

  const toggleHideImages = () =>
    setSettings((prev) => ({ ...prev, hideImages: !prev.hideImages }));

  const toggleHighlightLinks = () =>
    setSettings((prev) => ({ ...prev, highlightLinks: !prev.highlightLinks }));

  const toggleHighlightHeadings = () =>
    setSettings((prev) => ({ ...prev, highlightHeadings: !prev.highlightHeadings }));

  const toggleKeyboardNavigation = () =>
    setSettings((prev) => ({ ...prev, keyboardNavigation: !prev.keyboardNavigation }));

  const toggleStopAnimations = () =>
    setSettings((prev) => ({ ...prev, stopAnimations: !prev.stopAnimations }));

  const toggleBigCursor = () =>
    setSettings((prev) => ({ ...prev, bigCursor: !prev.bigCursor }));

  const toggleReadingGuide = () =>
    setSettings((prev) => ({ ...prev, readingGuide: !prev.readingGuide }));

  const resetAll = async () => {
    setSettings(defaultSettings);

    try {
      await AsyncStorage.removeItem(STORAGE_KEY);
      if (Platform.OS === 'web') {
        removeFromLocalStorage(WEB_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to clear accessibility settings:', error);
    }
  };

  const value = useMemo<AccessibilityContextValue>(
    () => ({
      settings,
      updateFontSize,
      increaseFontSize,
      decreaseFontSize,
      setLetterSpacing,
      setLineHeight,
      setTextAlign,
      toggleReadableFont,
      setContrastMode,
      toggleGrayscale,
      toggleInvertColors,
      toggleHideImages,
      toggleHighlightLinks,
      toggleHighlightHeadings,
      toggleKeyboardNavigation,
      toggleStopAnimations,
      toggleBigCursor,
      toggleReadingGuide,
      resetAll,
    }),
    [settings],
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility(): AccessibilityContextValue {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    throw new Error(
      'useAccessibility must be used within an AccessibilityProvider',
    );
  }
  return ctx;
}
